import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import styles from './Footer.module.scss';
import {
  FOOTER_ABOUT_US_URL,
  FOOTER_BLOGS_URL,
  FOOTER_CONTACT_US_URL,
  FOOTER_FAQ_URL,
  FOOTER_PRIVACY_URL,
  FOOTER_REFUND_POLICY_URL,
  FOOTER_SHIPPING_DELIVERY_URL,
  FOOTER_TERMS_URL,
  isExternalFooterUrl,
} from '@utils/footerEnvLinks';

function EnvFooterLink({ url, children }: { url: string; children: React.ReactNode }) {
  if (isExternalFooterUrl(url)) {
    return <a href={url}>{children}</a>;
  }
  return <Link to={url}>{children}</Link>;
}

const Footer: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    const updateTheme = () => {
      const currentTheme = document.documentElement.getAttribute('data-theme') as 'light' | 'dark' || 'dark';
      setTheme(currentTheme);
    };

    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer className={styles.footer} data-theme={theme}>
      <div className={styles.footerContent}>
        <div className={styles.footerColumn}>
          <h3 className={styles.footerTitle}>About Us</h3>
          <ul className={styles.footerLinks}>
            <li>
              <a href={FOOTER_ABOUT_US_URL}>About Oneplay</a>
            </li>
            <li>
              <EnvFooterLink url={FOOTER_BLOGS_URL}>Blog</EnvFooterLink>
            </li>
            <li>
              <EnvFooterLink url={FOOTER_CONTACT_US_URL}>Contact Us</EnvFooterLink>
            </li>
          </ul>
        </div>

        <div className={styles.footerColumn}>
          <h3 className={styles.footerTitle}>Support</h3>
          <ul className={styles.footerLinks}>
            <li>
              <EnvFooterLink url={FOOTER_FAQ_URL}>FAQ</EnvFooterLink>
            </li>
            <li>
              <Link to="/activation-guides">Activation Guides</Link>
            </li>
            <li>
              <EnvFooterLink url={FOOTER_SHIPPING_DELIVERY_URL}>Shipping Info</EnvFooterLink>
            </li>
            <li>
              <EnvFooterLink url={FOOTER_REFUND_POLICY_URL}>Refund Policy</EnvFooterLink>
            </li>
          </ul>
        </div>

        <div className={styles.footerColumn}>
          <h3 className={styles.footerTitle}>Stay Updated</h3>
          <div className={styles.socialMedia}>
            <a
              href="https://www.facebook.com/ONEPLAYIN"
              className={styles.socialLink}
              aria-label="OnePlay on Facebook"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
            <a
              href="https://www.instagram.com/oneplay.in?igshid=YmMyMTA2M2Y%3D"
              className={styles.socialLink}
              aria-label="OnePlay on Instagram"
            >
              <i className="fab fa-instagram"></i>
            </a>
            <a
              href="https://x.com/CloudOneplay"
              className={styles.socialLink}
              aria-label="OnePlay on X (Twitter)"
            >
              <i className="fab fa-x-twitter"></i>
            </a>
            <a
              href="https://medium.com/@oneplay_gaming"
              className={styles.socialLink}
              aria-label="OnePlay Blog on Medium"
            >
              <i className="fab fa-medium"></i>
            </a>
            <a
              href={process.env.REACT_APP_DISCORD_INVITE_URL || 'https://discord.com/invite/Cg4ZskStE2'}
              className={styles.socialLink}
              aria-label="Join Discord Channel"
            >
              <i className="fab fa-discord"></i>
            </a>
          </div>
        </div>
      </div>

      <div className={styles.footerBottom}>
        <div className={styles.footerBottomContent}>
          <p className={styles.copyright}>
            © {new Date().getFullYear()} Oneplay Store. All rights reserved.
          </p>
          <div className={styles.footerBottomLinks}>
            <EnvFooterLink url={FOOTER_PRIVACY_URL}>Privacy Policy</EnvFooterLink>
            <span className={styles.separator}>|</span>
            <EnvFooterLink url={FOOTER_TERMS_URL}>Terms of Service</EnvFooterLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

